import { tryPostureForPrompt, cashTRYNow } from '../modules/cashflow.js';
/**
 * ai-copilot.js — AI Copilot: Proactive Trading Intelligence (P4)
 *
 * Hybrid scanner + recommendation engine that:
 * 1. Fetches TA indicators from /api/ta for all portfolio tickers
 * 2. Combines TA data + portfolio state + market intel + open trades
 * 3. Sends structured context to LLM for actionable recommendations
 * 4. Stores results in S.aiCopilot for UI consumption
 * 5. Generates alerts for SL/TP proximity, trend reversals, etc.
 *
 * Architecture: Proactive "pushing" system — AI initiates, user confirms.
 * Execution goes through P3 Chat→Action (detectAction → confirmCard → execute).
 *
 * Exports:
 * - runCopilotScan(opts)           — full scan: TA fetch + LLM analysis
 * - getCopilotRecommendations()    — get current recommendations
 * - getCopilotAlerts()             — get active alerts
 * - dismissRecommendation(id)      — dismiss a recommendation
 * - dismissAlert(id)               — dismiss an alert
 * - getCopilotStatus()             — scan status / last run info
 * - clearCopilotData()             — reset all copilot state
 */

import { S, save } from '../core/state.js';
import { chatCompletion, isAIConfigured } from '../core/ai.js';
import { uid, f, toUSD as _toUSD, openTrades as _openTrades, fxUSDTRY, fxEURTRY, promptSafe } from '../core/utils.js';
import { ledgerRecEmitted, ledgerMergeDiff, ledgerRecDismissed } from '../core/decision-ledger.js';
import { transformForMandate, mandateOfPortfolio } from '../core/alpha-score.js';
import { hVal, hPnl, hTotUSD } from '../core/math.js';
import { toast } from '../ui/layout.js';
import { renderOnly } from '../app/render-loop.js';
import { getMarketIntel } from './market-intel.js';
import { fetchAdvisoryNews } from './news-ingest.js';
import { analyzeNewsImpact } from './ai-impact.js';
import { runRecommendEngine } from './ai-recommend.js';

// ─── CONSTANTS ──────────────────────────────────────────────────

const TA_ENDPOINT = '/api/ta';
const FA_ENDPOINT = '/api/fa';
const MAX_TICKERS = 15;
const MAX_RECOMMENDATIONS = 20;
const MAX_ALERTS = 50;
const MAX_SCAN_HISTORY = 30;
const SCAN_COOLDOWN_MS = 2 * 60 * 1000; // 2 min between scans

// ─── STATE ──────────────────────────────────────────────────────

let _scanning = false;
let _lastScanTs = 0;
let _abortController = null;

// ─── TA DATA FETCHER ────────────────────────────────────────────

/**
 * Fetch TA indicators from our Netlify function for given tickers.
 * @param {string[]} tickers — e.g. ['VOO','AAPL','BTC']
 * @param {string} period — '1mo','3mo','6mo','1y'
 * @returns {Promise<Object>} { data: { VOO: {...}, ... }, ts, period, count }
 */
async function _fetchTA(tickers, period = '3mo') {
  if (!tickers.length) return { data: {}, ts: Date.now(), period, count: 0 };

  const url = `${TA_ENDPOINT}?tickers=${encodeURIComponent(tickers.join(','))}&period=${period}`;
  const res = await fetch(url, {
    signal: _abortController?.signal || AbortSignal.timeout(30000),
  });

  if (!res.ok) {
    throw new Error(`TA API error: ${res.status}`);
  }

  return res.json();
}

// ─── PORTFOLIO CONTEXT BUILDER ──────────────────────────────────

/**
 * Build a list of tickers from the user's portfolio + open trades.
 * Returns unique tickers mapped to their TA-compatible names.
 */
function _getPortfolioTickers() {
  const tickers = new Set();

  // Holdings (skip Cash, Fund types — no Yahoo data)
  const safeH = (S.holdings || []).filter(h => h && !h._del);
  safeH.forEach(h => {
    if (!h.ticker) return;
    const tp = (h.type || '').toLowerCase();
    if (tp === 'cash') return;
    // Turkish mutual funds have no Yahoo ticker
    if (tp === 'fund' && (h.currency === 'TRY')) return;
    tickers.add(h.ticker);
  });

  // Open trades
  const safeT = (S.trades || []).filter(t => t && !t._del && t.status !== 'Closed');
  safeT.forEach(t => {
    if (t.ticker && t.type !== 'TimeDeposit') tickers.add(t.ticker);
  });

  return [...tickers].slice(0, MAX_TICKERS);
}

/**
 * Build rich portfolio context for LLM consumption.
 */
function _buildPortfolioContext() {
  const fxR = fxUSDTRY();
  const safeH = (S.holdings || []).filter(h => h && !h._del);
  const safeT = (S.trades || []).filter(t => t && !t._del);

  // Portfolio mandate map (v0.3): the model must know which holdings are accumulate-mandate
  // so it stops phrasing exits in prose. transformForMandate stays as the hard safety net.
  const _mandateByPid = {};
  (S.portfolios || []).forEach(p => { _mandateByPid[p.id] = mandateOfPortfolio(p); });

  // Holdings summary
  const holdings = safeH.map(h => {
    try {
      const v = hVal(h);
      const pl = hPnl(h);
      if (!v?.vU && !h.qty) return null;
      return {
        ticker: h.ticker, type: h.type || 'Other', subType: h.subType || '',
        mandate: _mandateByPid[h.portfolioId] || 'active',
        currency: h.currency || 'USD', qty: h.qty || 0,
        avgCost: +(h.avgCost || 0).toFixed(2),
        // hVal returns { vL, vU } — there is no px, and there is no h.allocation either
        // (the schema field is allocationPct). Both read as 0, so every holding reached the
        // model as "px=0.00, target=0%, drift=0%": a book at total loss with no target
        // model to rebalance against, on every scan. Same phantom-field class as the
        // digest's v.iU fixed earlier today. (audit 2026-08-01)
        currentPrice: +((S.prices && S.prices[h.ticker] && S.prices[h.ticker].price) || 0).toFixed(2),
        valueUSD: +(v?.vU || 0).toFixed(2),
        pnlPct: +(pl?.pnlP || 0).toFixed(2),
        targetWeight: h.allocationPct || 0,
      };
    } catch (_e) { return null; }
  }).filter(Boolean).sort((a, b) => b.valueUSD - a.valueUSD);

  // The denominator must be the SAME set as the numerators. Accumulating during the map
  // left a holding that later threw inside the total, understating every other position's
  // weight and producing spurious rebalance recommendations. (audit 2026-08-01)
  const totalUSD = holdings.reduce((s, h) => s + (h.valueUSD || 0), 0);

  // Add actual weights
  holdings.forEach(h => {
    h.actualWeight = totalUSD > 0 ? +((h.valueUSD / totalUSD) * 100).toFixed(1) : 0;
    h.weightDrift = h.targetWeight > 0 ? +(h.actualWeight - h.targetWeight).toFixed(1) : 0;
  });

  // Open trades
  const openT = _openTrades(safeT)
    .map(t => {
      const px = S.prices?.[t.ticker]?.price || t.entry;
      const entry = t.entry || 0;
      const lev = t.leverage || 1;
      const isLong = (t.side || 'Long') === 'Long';
      const pnlPct = entry > 0 ? ((isLong ? (px - entry) : (entry - px)) / entry * 100 * lev) : 0;
      return {
        ticker: t.ticker, type: t.type || 'Other', side: t.side || 'Long',
        entry, qty: t.qty, leverage: lev, currency: t.currency || 'USD',
        sl: t.sl || null, tp1: t.tp1 || null, tp2: t.tp2 || null,
        currentPrice: +px.toFixed(4),
        unrealizedPnlPct: +pnlPct.toFixed(2),
        status: t.status || 'Open',
      };
    });

  // Cash
  const cash = {
    USD: +(S.cash?.USD || 0).toFixed(2),
    // NOW, not the last bank reading — see cashTRYNow().
    TRY: +cashTRYNow().toFixed(2),
    EUR: +(S.cash?.EUR || 0).toFixed(2),
  };

  return {
    totalValueUSD: +totalUSD.toFixed(2),
    fxRate: fxR,
    holdingsCount: safeH.length,
    holdings,
    openTrades: openT,
    cash,
  };
}

/**
 * Truncation that says so.
 *
 * The portfolio section sent the model `holdings.slice(0, 20)` under a header carrying the
 * FULL book total. The owner holds 44 positions. So the model read 20 rows whose weights sum
 * to well under 100%, beside a total that counted all 44, and was never told anything was
 * missing — the arithmetic invited it to infer a large unallocated remainder that does not
 * exist, and to grade portfolioHealth and rebalance on 45% of the book while believing it saw
 * everything. A cap the reader cannot see is not a cap, it is a misstatement.
 *
 * Standing rule R6: truncation self-identifies. These two helpers are how that is enforced in
 * every prompt section — the note goes in the HEADER, where the model reads the scope before
 * it reads the rows.
 */
const PORTFOLIO_ROWS = 40;   // measured against a 44-position book, not guessed
function _capNote(total, shown, unit, omittedUSD) {
  if (shown >= total) return '';
  const n = total - shown;
  const money = omittedUSD > 0 ? ', $' + Math.round(omittedUSD).toLocaleString('en-US') : '';
  return ` — SHOWING TOP ${shown} OF ${total} ${unit} BY VALUE; ${n} omitted${money}. Do not treat the missing rows as cash or as unallocated.`;
}
function _capNoteSimple(total, shown, unit) {
  if (shown >= total) return '';
  return ` — showing ${shown} of ${total} ${unit}; ${total - shown} omitted`;
}

/**
 * The portfolio block, once. It was copy-pasted into both _composeScanMessage and
 * _composeDeepScanMessage, which is how the same silent cap shipped twice.
 */
function _portfolioSection(portfolio, cap) {
  const all = portfolio.holdings || [];
  if (!all.length) return null;
  const shown = all.slice(0, cap);
  const omittedUSD = all.slice(cap).reduce((a, h) => a + (h.valueUSD || 0), 0);
  // promptSafe on the text fields: a ticker can arrive from importMoCsv, a restored backup or
  // a cloud merge, and this block is pipe- and line-structured. (2026-08-14)
  const lines = shown.map(h =>
    `${promptSafe(h.ticker, 16)}: $${h.valueUSD} | ${promptSafe(h.type, 24)} | qty=${h.qty} | avg=${h.avgCost} | px=${h.currentPrice} | P&L=${h.pnlPct}% | w=${h.actualWeight}% (target=${h.targetWeight}% drift=${h.weightDrift}%)`
  );
  return `=== PORTFOLIO (total $${portfolio.totalValueUSD}, FX=${portfolio.fxRate}`
    + _capNote(all.length, shown.length, 'positions', omittedUSD) + `) ===\n` + lines.join('\n');
}

// ─── COPILOT SYSTEM PROMPT ──────────────────────────────────────

const COPILOT_SYSTEM_PROMPT = `You are SPEKTRX Copilot — a proactive trading intelligence engine for a personal portfolio tracker.

Your role is PROACTIVE: you push insights, warnings, and actionable trade ideas. You are not passive.

CONTEXT:
- User is a senior investor managing a diversified portfolio: US equities/ETFs, BIST Turkish stocks, mutual funds, crypto (BTC/ETH), and commodities (gold, silver).
- Base currency: USD, with TRY equivalent tracking.
- User uses leveraged crypto trades (3-5x).
- Monthly DCA strategy for long-term holdings.
- Active trades with defined SL/TP levels.

YOU WILL RECEIVE:
1. Technical Analysis data: RSI, SMA(20/50/200), EMA(12/26), MACD, Bollinger Bands, support/resistance, trend classification for each ticker.
2. Portfolio state: holdings, weights, P&L, open trades with SL/TP levels, cash position.
3. Market intelligence: crypto sentiment (Fear & Greed), market breadth, FX rates.

YOUR OUTPUT (strict JSON):
{
  "recommendations": [
    {
      "type": "buy|sell|hold|rebalance|alert|hedge|take_profit|stop_loss|scale_in|dca_adjust",
      "ticker": "VOO",
      "urgency": "high|medium|low",
      "title": "Short action title (max 60 chars)",
      "reasoning": "2-3 sentence explanation grounded in TA data",
      "action": "Specific actionable instruction (e.g., 'Buy 2 shares of VOO at market')",
      "metrics": {
        "rsi": 45.2,
        "trend": "bullish",
        "distanceToSL": -5.2,
        "distanceToTP1": 8.1
      }
    }
  ],
  "alerts": [
    {
      "type": "sl_proximity|tp_proximity|trend_reversal|oversold|overbought|bollinger_squeeze|support_break|resistance_break|weight_drift|high_concentration",
      "ticker": "AAPL",
      "severity": "critical|warning|info",
      "message": "Clear, concise alert message"
    }
  ],
  "marketSummary": "2-3 sentence overall market assessment",
  "portfolioHealth": "healthy|caution|warning|critical"
}

RULES:
1. Ground every recommendation in specific TA data — cite RSI values, SMA crossovers, trend direction.
2. Flag SL/TP proximity: if price is within 3% of SL → critical alert. Within 5% of TP → take-profit opportunity.
3. Detect trend reversals: if trend changed from bullish to bearish (or vice versa) while user holds a position.
4. Identify oversold opportunities (RSI < 30) and overbought warnings (RSI > 70).
5. Check weight drift: if actual weight deviates >3% from target allocation, suggest rebalance.
6. For leveraged trades, multiply urgency — a 3% adverse move at 5x leverage = 15% loss.
7. Maximum 8 recommendations and 10 alerts per scan.
8. All text in English. No motivational content. Be direct and specific.
9. Never give generic financial advice. Ground everything in the actual data provided.
10. If data is insufficient for a ticker, skip it rather than guessing.
11. MANDATE POLICY (hard rule): each holding carries a "mandate" field. Holdings with mandate "accumulate" are on a 5-10 year monthly DCA plan — NEVER recommend selling, trimming, taking profit, or exiting them, in ANY field including reasoning and action prose. For overbought/bearish signals on accumulate holdings, the only valid framings are: pause/redirect this month's DCA, note the signal as informational, or suggest steering DCA toward underweight components. Exit language on accumulate holdings is a policy violation. Holdings with mandate "active" (and all open trades) may receive the full range of directives.`;

// ─── CORE SCAN ENGINE ───────────────────────────────────────────

/**
 * Run a full copilot scan: fetch TA → build context → LLM analysis → store results.
 *
 * @param {Object} opts
 * @param {string} opts.period — TA lookback period ('1mo','3mo','6mo','1y')
 * @param {boolean} opts.force — bypass cooldown
 * @returns {Promise<Object>} scan result
 */
export async function runCopilotScan(opts = {}) {
  // Guard: AI must be configured
  if (!isAIConfigured()) {
    toast('AI not configured. Go to Settings → AI.', 'a-r');
    return { ok: false, error: 'AI not configured' };
  }

  // Guard: prevent concurrent scans
  if (_scanning) {
    toast('Scan already in progress...', 'a-y');
    return { ok: false, error: 'Scan in progress' };
  }

  // Guard: cooldown (unless forced)
  if (!opts.force && (Date.now() - _lastScanTs) < SCAN_COOLDOWN_MS) {
    const waitSec = Math.ceil((SCAN_COOLDOWN_MS - (Date.now() - _lastScanTs)) / 1000);
    toast(`Cooldown: wait ${waitSec}s before next scan`, 'a-y');
    return { ok: false, error: 'Cooldown active' };
  }

  _scanning = true;
  _abortController = new AbortController();
  const scanStart = Date.now();
  const period = opts.period || '3mo';

  try {
    // Step 1: Get portfolio tickers
    const tickers = _getPortfolioTickers();
    if (!tickers.length) {
      toast('No scannable tickers in portfolio', 'a-y');
      return { ok: false, error: 'No tickers' };
    }

    // Step 2: Fetch TA data + market intel + held-instrument news in parallel
    const [taResult, marketIntel, newsResult] = await Promise.allSettled([
      _fetchTA(tickers, period),
      getMarketIntel({ all: true }),
      fetchAdvisoryNews(),
    ]);

    const taData = taResult.status === 'fulfilled' ? taResult.value : { data: {} };
    const market = marketIntel.status === 'fulfilled' ? marketIntel.value : {};
    const news = newsResult.status === 'fulfilled' ? newsResult.value : { items: [] };

    // Step 2b (P2): map news → graded, evidence-backed impacts on held instruments.
    // On failure/empty the scan falls back to raw headlines inside _composeScanMessage.
    let impacts = [];
    if (news.items && news.items.length) {
      try {
        const ir = await analyzeNewsImpact(news.items);
        impacts = ir.impacts || [];
        // The stage returns {ok:false} instead of throwing on its most common failure,
        // so the surrounding catch is dead for exactly the case that matters.
        if (!ir.ok) _noteImpactFailure(ir.error || 'impact stage returned not-ok');
      } catch (e) {
        _noteImpactFailure(e.message);
      }
    } else {
      _noteImpactFailure('news feed returned 0 items — impact stage was not run');
    }

    // Cache F&G for scenario evaluation.
    // _fetchFearGreed() swallows a proxy failure and returns its own (possibly empty)
    // cache, so results.fearGreed can be undefined while a PREVIOUS value still sits in
    // S.aiCopilot._fgCache. Leaving that silently is how a weeks-old sentiment reading
    // ends up sizing a Bonus deployment tranche. Say so instead. (audit 2026-08-01)
    if (market.fearGreed) {
      cacheFearGreed(market.fearGreed);
    } else {
      console.warn('[Copilot] Fear & Greed not refreshed this scan — the cached value keeps its previous (unverifiable) age');
      if (S.aiCopilot) S.aiCopilot._fgCacheTs = null;
    }

    // Step 3: Build portfolio context
    const portfolio = _buildPortfolioContext();

    // Step 4: Compose LLM message
    const userMessage = _composeScanMessage(taData.data || {}, portfolio, market, news, impacts);

    // Step 5: Call LLM
    const response = await chatCompletion([
      { role: 'system', content: COPILOT_SYSTEM_PROMPT },
      { role: 'user', content: userMessage },
    ], {
      maxTokens: 4096,
      temperature: 0.2,
      signal: _abortController.signal,
      timeoutMs: 180000, // hard 3-min cap even with the cancel controller attached (audit W1-4)
    });

    // Step 6: Parse response
    const parsed = _parseResponse(response.content);
    // A response we could not parse is a FAILED scan, not a scan that found nothing.
    // Storing it stamped a fresh lastScan.ts while _storeResults kept every previous
    // recommendation (newKeys is empty, so nothing is superseded), so yesterday's cards
    // rendered under "scan just now" and the toast went green with "0 recommendations".
    // parseError was set and never read anywhere. (audit 2026-08-01)
    if (parsed.parseError) {
      throw new Error('AI returned an unparseable response — scan discarded rather than stored as empty');
    }

    // Step 7: Store results
    _storeResults(parsed, taData, scanStart);

    // Step 7b (P3): deterministic engine recs merged on top of LLM recs
    // (engine wins on ticker+type collisions; stale engine recs die with their trigger)
    try {
      runRecommendEngine();
      renderOnly('dash', 'advisory');
    } catch (e) {
      console.warn('[Copilot] Recommend engine failed (LLM recs still stored):', e.message);
    }

    _lastScanTs = Date.now();
    const elapsed = ((Date.now() - scanStart) / 1000).toFixed(1);
    toast(`Copilot scan complete (${elapsed}s) — ${(parsed.recommendations || []).length} recommendations`, 'a-g');

    return {
      ok: true,
      recommendations: parsed.recommendations?.length || 0,
      alerts: parsed.alerts?.length || 0,
      elapsed: +elapsed,
      model: response.model,
    };

  } catch (e) {
    if (e.name === 'AbortError') {
      toast('Scan cancelled', 'a-y');
      return { ok: false, error: 'Cancelled' };
    }
    console.error('[Copilot] Scan failed:', e);
    toast('Copilot scan failed: ' + (e.message || 'Unknown error'), 'a-r');
    return { ok: false, error: e.message };
  } finally {
    _scanning = false;
    _abortController = null;
  }
}

/**
 * Cancel a running scan.
 */
export function cancelCopilotScan() {
  if (_abortController) {
    _abortController.abort();
    _scanning = false;
  }
}

// ─── FA DATA FETCHER ───────────────────────────────────────────

/**
 * Fetch fundamental analysis data from our Netlify FA function.
 * @param {string[]} tickers
 * @returns {Promise<Object>} { data: { AAPL: { priceTarget, grades, estimates }, ... } }
 */
async function _fetchFA(tickers) {
  if (!tickers.length) return { data: {} };
  try {
    const url = `${FA_ENDPOINT}?tickers=${encodeURIComponent(tickers.join(','))}`;
    const res = await fetch(url, {
      signal: _abortController?.signal || AbortSignal.timeout(20000),
    });
    if (!res.ok) return { data: {} };
    return res.json();
  } catch (_e) {
    return { data: {} };
  }
}

// ─── DEEP SCAN (Phase 4) ──────────────────────────────────────

/**
 * Run a deep copilot scan: TA + FA + optional IBKR position data.
 * This is the comprehensive scan mode — called by scheduled tasks or manually.
 *
 * @param {Object} opts
 * @param {string} opts.period — TA lookback period
 * @param {Object} opts.ibkrPositions — optional IBKR positions data (injected by Cowork task)
 * @param {Object} opts.ibkrBalances — optional IBKR balances data
 * @param {boolean} opts.force — bypass cooldown
 * @returns {Promise<Object>} scan result
 */
export async function runDeepScan(opts = {}) {
  if (!isAIConfigured()) {
    toast('AI not configured. Go to Settings → AI.', 'a-r');
    return { ok: false, error: 'AI not configured' };
  }
  if (_scanning) {
    toast('Scan already in progress...', 'a-y');
    return { ok: false, error: 'Scan in progress' };
  }
  if (!opts.force && (Date.now() - _lastScanTs) < SCAN_COOLDOWN_MS) {
    const waitSec = Math.ceil((SCAN_COOLDOWN_MS - (Date.now() - _lastScanTs)) / 1000);
    toast(`Cooldown: wait ${waitSec}s before next scan`, 'a-y');
    return { ok: false, error: 'Cooldown active' };
  }

  _scanning = true;
  _abortController = new AbortController();
  const scanStart = Date.now();
  const period = opts.period || '3mo';

  try {
    const tickers = _getPortfolioTickers();
    if (!tickers.length) {
      toast('No scannable tickers in portfolio', 'a-y');
      return { ok: false, error: 'No tickers' };
    }

    // Parallel fetch: TA + FA + Market Intel
    const [taResult, faResult, marketIntel] = await Promise.allSettled([
      _fetchTA(tickers, period),
      _fetchFA(tickers),
      getMarketIntel({ all: true }),
    ]);

    const taData = taResult.status === 'fulfilled' ? taResult.value : { data: {} };
    const faData = faResult.status === 'fulfilled' ? faResult.value : { data: {} };
    const market = marketIntel.status === 'fulfilled' ? marketIntel.value : {};

    if (market.fearGreed) cacheFearGreed(market.fearGreed);

    const portfolio = _buildPortfolioContext();

    // Build IBKR reconciliation if positions provided
    const ibkrContext = _buildIBKRContext(opts.ibkrPositions, opts.ibkrBalances, portfolio);

    // Compose enriched message with TA + FA + IBKR
    const userMessage = _composeDeepScanMessage(taData.data || {}, faData.data || {}, portfolio, market, ibkrContext);

    // Call LLM with enhanced system prompt
    const response = await chatCompletion([
      { role: 'system', content: DEEP_SCAN_SYSTEM_PROMPT },
      { role: 'user', content: userMessage },
    ], {
      maxTokens: 4000,
      temperature: 0.2,
      signal: _abortController.signal,
    });

    const parsed = _parseResponse(response.content);
    _storeResults(parsed, taData, scanStart);

    // Store IBKR drift alerts if any
    if (ibkrContext.driftAlerts?.length) {
      _injectIBKRAlerts(ibkrContext.driftAlerts);
    }

    _lastScanTs = Date.now();
    const elapsed = ((Date.now() - scanStart) / 1000).toFixed(1);
    const faCount = Object.values(faData.data || {}).filter(d => d.hasData).length;
    toast(`Deep scan complete (${elapsed}s) — ${(parsed.recommendations || []).length} recs, ${faCount} FA tickers`, 'a-g');

    return {
      ok: true,
      deep: true,
      recommendations: parsed.recommendations?.length || 0,
      alerts: parsed.alerts?.length || 0,
      faCount,
      ibkrReconciled: !!opts.ibkrPositions,
      elapsed: +elapsed,
      model: response.model,
    };
  } catch (e) {
    if (e.name === 'AbortError') {
      toast('Deep scan cancelled', 'a-y');
      return { ok: false, error: 'Cancelled' };
    }
    console.error('[Copilot] Deep scan failed:', e);
    toast('Deep scan failed: ' + (e.message || 'Unknown error'), 'a-r');
    return { ok: false, error: e.message };
  } finally {
    _scanning = false;
    _abortController = null;
  }
}

/**
 * Build IBKR reconciliation context — compare broker positions vs app holdings.
 */
function _buildIBKRContext(positions, balances, portfolio) {
  if (!positions || !Array.isArray(positions)) return { available: false, driftAlerts: [] };

  const driftAlerts = [];
  const ts = new Date().toISOString();
  const posMap = {};

  // Map IBKR positions by ticker
  positions.forEach(p => {
    const ticker = p.ticker || p.symbol || '';
    if (!ticker) return;
    posMap[ticker] = {
      ticker,
      ibkrQty: p.position || p.quantity || 0,
      ibkrAvgCost: p.avgCost || p.averageCost || 0,
      ibkrMktValue: p.mktValue || p.marketValue || 0,
      ibkrUnrealizedPnl: p.unrealizedPnl || 0,
    };
  });

  // Compare with app holdings.
  // Alias (2026-07-24): UCITS rows use Yahoo LSE symbols (CSPX.L) while IBKR reports the
  // bare exchange symbol (CSPX) — without stripping the suffix every UCITS position would
  // flag as a false mismatch from the first Option A buy onward.
  const _aliasOf = t => (t || '').replace(/\.(L|IL)$/i, '');
  const reconciliation = [];
  (portfolio.holdings || []).forEach(h => {
    const ibkr = posMap[h.ticker] || posMap[_aliasOf(h.ticker)];
    if (!ibkr) {
      // In app but not in IBKR — might be non-IBKR asset
      return;
    }

    const qtyDiff = Math.abs((h.qty || 0) - ibkr.ibkrQty);
    const costDiff = Math.abs((h.avgCost || 0) - ibkr.ibkrAvgCost);
    const qtyMatch = qtyDiff < 0.001;
    const costMatch = costDiff < 0.01;

    reconciliation.push({
      ticker: h.ticker,
      appQty: h.qty || 0,
      ibkrQty: ibkr.ibkrQty,
      appAvgCost: h.avgCost || 0,
      ibkrAvgCost: ibkr.ibkrAvgCost,
      qtyMatch,
      costMatch,
    });

    if (!qtyMatch) {
      driftAlerts.push({
        id: uid(),
        type: 'ibkr_drift',
        ticker: h.ticker,
        severity: 'warning',
        message: `IBKR position drift: ${h.ticker} app=${h.qty} vs IBKR=${ibkr.ibkrQty} (diff=${qtyDiff.toFixed(4)})`,
        source: 'ibkr_reconciliation',
        createdAt: ts,
        dismissed: false,
      });
    }
  });

  return {
    available: true,
    positions: posMap,
    reconciliation,
    driftAlerts,
    balances: balances || null,
  };
}

/**
 * Inject IBKR drift alerts into the alert stream.
 */
function _injectIBKRAlerts(alerts) {
  if (!alerts.length) return;
  const existing = (S.aiCopilot.alerts || []).filter(a => !a.dismissed);
  // Remove previous IBKR drift alerts
  const kept = existing.filter(a => a.source !== 'ibkr_reconciliation');
  S.aiCopilot.alerts = [...alerts, ...kept].slice(0, MAX_ALERTS);
  save();
}

// ─── DEEP SCAN SYSTEM PROMPT ──────────────────────────────────

const DEEP_SCAN_SYSTEM_PROMPT = `${COPILOT_SYSTEM_PROMPT}

ADDITIONAL CONTEXT (DEEP SCAN MODE):
You now also receive FUNDAMENTAL ANALYSIS data:
- Analyst price targets (high/low/consensus/median)
- Analyst grade consensus (buy/hold/sell counts + recent grades)
- Financial estimates (EPS, revenue forecasts)

And optionally IBKR BROKER RECONCILIATION data:
- Actual broker positions vs app-tracked positions
- Quantity and cost-basis mismatches

ENHANCED RULES for deep scan:
11. Compare current price to analyst price target consensus — note if significantly above (overvalued risk) or below (upside potential).
12. If analyst consensus is "Strong Buy" with price well below target, flag as scale-in opportunity.
13. If analyst consensus is "Sell" or "Strong Sell", flag as risk alert regardless of TA.
14. If IBKR position data shows drift from app data, flag as reconciliation alert.
15. Combine TA and FA signals — a stock that is both technically oversold (RSI<30) AND has strong analyst buy consensus is a high-conviction opportunity.
16. For ETFs, focus on TA since analyst ratings are less relevant.
17. Maximum 10 recommendations and 12 alerts for deep scan (more data = more signals).
18. MANDATE POLICY applies unchanged in deep scan: never phrase exits/trims/take-profits on mandate "accumulate" holdings — even when FA consensus says sell. Frame as DCA guidance instead.`;

// ─── DEEP SCAN MESSAGE COMPOSER ────────────────────────────────

function _composeDeepScanMessage(taData, faData, portfolio, market, ibkrContext) {
  // Start with standard TA sections
  const sections = [];

  // === TA DATA ===
  const taEntries = Object.entries(taData);
  if (taEntries.length) {
    const taLines = taEntries.map(([tk, d]) => {
      if (d.error) return `${tk}: ERROR — ${d.error}`;
      const parts = [
        `${tk}: $${d.price}`,
        `chg=${d.changePct}%`,
        `RSI=${d.rsi ?? 'N/A'}`,
        `trend=${d.trend}`,
        `SMA20=${d.sma20 ?? '-'} SMA50=${d.sma50 ?? '-'} SMA200=${d.sma200 ?? '-'}`,
      ];
      if (d.macd) parts.push(`MACD=${d.macd.line}`);
      if (d.bollinger) parts.push(`BB=[${d.bollinger.lower},${d.bollinger.middle},${d.bollinger.upper}]`);
      if (d.support) parts.push(`S=${d.support}`);
      if (d.resistance) parts.push(`R=${d.resistance}`);
      parts.push(`range=${d.rangePosition}%`);
      return parts.join(' | ');
    });
    sections.push('=== TECHNICAL ANALYSIS ===\n' + taLines.join('\n'));
  }

  // === FUNDAMENTAL ANALYSIS ===
  const faEntries = Object.entries(faData).filter(([_k, v]) => v.hasData);
  if (faEntries.length) {
    const faLines = faEntries.map(([tk, d]) => {
      const parts = [`${tk}:`];
      if (d.priceTarget) {
        parts.push(`PT: low=${d.priceTarget.targetLow} med=${d.priceTarget.targetMedian} high=${d.priceTarget.targetHigh} cons=${d.priceTarget.targetConsensus}`);
      }
      if (d.grades) {
        parts.push(`Grades: ${d.grades.consensus} (${d.grades.counts.buy}B/${d.grades.counts.hold}H/${d.grades.counts.sell}S)`);
        if (d.grades.recentGrades?.length) {
          parts.push(`Recent: ${d.grades.recentGrades.map(g => `${g.company}→${g.grade}`).join(', ')}`);
        }
      }
      if (d.estimates) {
        parts.push(`Est EPS=${d.estimates.estimatedEpsAvg ?? '-'} Rev=${d.estimates.estimatedRevenueAvg ?? '-'}`);
      }
      return parts.join(' | ');
    });
    sections.push('=== FUNDAMENTAL ANALYSIS ===\n' + faLines.join('\n'));
  }

  // === PORTFOLIO ===
  {
    const ps = _portfolioSection(portfolio, PORTFOLIO_ROWS);
    if (ps) sections.push(ps);
  }

  // === OPEN TRADES ===
  if (portfolio.openTrades.length) {
    const tLines = portfolio.openTrades.map(t => {
      const parts = [
        `${t.ticker}: ${t.side} @ ${t.entry}`,
        `px=${t.currentPrice}`,
        `P&L=${t.unrealizedPnlPct}%`,
        `lev=${t.leverage}x`,
      ];
      if (t.sl) parts.push(`SL=${t.sl}`);
      if (t.tp1) parts.push(`TP1=${t.tp1}`);
      if (t.tp2) parts.push(`TP2=${t.tp2}`);
      return parts.join(' | ');
    });
    sections.push('=== OPEN TRADES ===\n' + tLines.join('\n'));
  }

  // === CASH ===
  sections.push(`=== CASH ===\nUSD: $${portfolio.cash.USD} | TRY: ₺${portfolio.cash.TRY} | EUR: €${portfolio.cash.EUR}` + (() => { const p = tryPostureForPrompt(); return p.tryFreeTRY == null
    ? `\nTRY commitment UNKNOWN (never reconciled) — do not treat the TRY balance as deployable.`
    : `\nTRY committed this month: ₺${p.tryCommittedTRY} (unpaid bills + un-transferred plan) | TRY free: ₺${p.tryFreeTRY} — only the free figure is deployable.`; })());

  // === IBKR RECONCILIATION ===
  if (ibkrContext.available && ibkrContext.reconciliation?.length) {
    const drifts = ibkrContext.reconciliation.filter(r => !r.qtyMatch || !r.costMatch);
    if (drifts.length) {
      const dLines = drifts.map(d =>
        `${d.ticker}: app_qty=${d.appQty} ibkr_qty=${d.ibkrQty} | app_cost=${d.appAvgCost} ibkr_cost=${d.ibkrAvgCost}`
      );
      sections.push('=== IBKR POSITION DRIFT ===\n' + dLines.join('\n'));
    }
    if (ibkrContext.balances) {
      sections.push(`=== IBKR BALANCES ===\n${JSON.stringify(ibkrContext.balances)}`);
    }
  }

  // === MARKET INTEL ===
  const mParts = [];
  if (market.fearGreed) {
    mParts.push(`Fear&Greed: ${market.fearGreed.value} (${market.fearGreed.classification})`);
  }
  if (market.cryptoGlobal) {
    mParts.push(`Crypto MCAP: $${(market.cryptoGlobal.totalMarketCapUSD / 1e12).toFixed(2)}T | BTC.D=${market.cryptoGlobal.btcDominance}%`);
  }
  if (market.internal) {
    const mb = market.internal.marketBreadth;
    if (mb) mParts.push(`Breadth: ${mb.gainers}↑ ${mb.losers}↓ ${mb.unchanged}→`);
    if (market.internal.fx && market.internal.fx.USDTRY) mParts.push(`USD/TRY=${market.internal.fx.USDTRY}`);
  }
  if (mParts.length) {
    sections.push('=== MARKET ===\n' + mParts.join('\n'));
  }

  return sections.join('\n\n');
}

// ─── MESSAGE COMPOSER ───────────────────────────────────────────

function _composeScanMessage(taData, portfolio, market, news, impacts) {
  const sections = [];

  // === TA DATA ===
  const taEntries = Object.entries(taData);
  if (taEntries.length) {
    const taLines = taEntries.map(([tk, d]) => {
      if (d.error) return `${tk}: ERROR — ${d.error}`;
      const parts = [
        `${tk}: $${d.price}`,
        `chg=${d.changePct}%`,
        `RSI=${d.rsi ?? 'N/A'}`,
        `trend=${d.trend}`,
        `SMA20=${d.sma20 ?? '-'} SMA50=${d.sma50 ?? '-'} SMA200=${d.sma200 ?? '-'}`,
      ];
      if (d.macd) parts.push(`MACD=${d.macd.line}`);
      if (d.bollinger) parts.push(`BB=[${d.bollinger.lower},${d.bollinger.middle},${d.bollinger.upper}]`);
      if (d.support) parts.push(`S=${d.support}`);
      if (d.resistance) parts.push(`R=${d.resistance}`);
      parts.push(`range=${d.rangePosition}%`);
      return parts.join(' | ');
    });
    sections.push('=== TECHNICAL ANALYSIS ===\n' + taLines.join('\n'));
  }

  // === PORTFOLIO ===
  {
    const ps = _portfolioSection(portfolio, PORTFOLIO_ROWS);
    if (ps) sections.push(ps);
  }

  // === OPEN TRADES ===
  if (portfolio.openTrades.length) {
    const tLines = portfolio.openTrades.map(t => {
      const parts = [
        `${t.ticker}: ${t.side} @ ${t.entry}`,
        `px=${t.currentPrice}`,
        `P&L=${t.unrealizedPnlPct}%`,
        `lev=${t.leverage}x`,
      ];
      if (t.sl) parts.push(`SL=${t.sl}`);
      if (t.tp1) parts.push(`TP1=${t.tp1}`);
      if (t.tp2) parts.push(`TP2=${t.tp2}`);
      return parts.join(' | ');
    });
    sections.push('=== OPEN TRADES ===\n' + tLines.join('\n'));
  }

  // === CASH ===
  sections.push(`=== CASH ===\nUSD: $${portfolio.cash.USD} | TRY: ₺${portfolio.cash.TRY} | EUR: €${portfolio.cash.EUR}` + (() => { const p = tryPostureForPrompt(); return p.tryFreeTRY == null
    ? `\nTRY commitment UNKNOWN (never reconciled) — do not treat the TRY balance as deployable.`
    : `\nTRY committed this month: ₺${p.tryCommittedTRY} (unpaid bills + un-transferred plan) | TRY free: ₺${p.tryFreeTRY} — only the free figure is deployable.`; })());

  // === MARKET INTEL ===
  const mParts = [];
  if (market.fearGreed) {
    mParts.push(`Fear&Greed: ${market.fearGreed.value} (${market.fearGreed.classification})`);
  }
  if (market.cryptoGlobal) {
    mParts.push(`Crypto MCAP: $${(market.cryptoGlobal.totalMarketCapUSD / 1e12).toFixed(2)}T | BTC.D=${market.cryptoGlobal.btcDominance}%`);
  }
  if (market.internal) {
    const mb = market.internal.marketBreadth;
    if (mb) mParts.push(`Breadth: ${mb.gainers}↑ ${mb.losers}↓ ${mb.unchanged}→`);
    if (market.internal.fx) {
      if (market.internal.fx.USDTRY) mParts.push(`USD/TRY=${market.internal.fx.USDTRY}`);
    }
  }
  if (mParts.length) {
    sections.push('=== MARKET ===\n' + mParts.join('\n'));
  }

  // === NEWS IMPACTS (P2 pipeline — graded + evidence-backed) ===
  if (impacts && impacts.length) {
    const iShown = impacts.slice(0, 12);
    const iLines = iShown.map(im =>
      `[${im.tickers.join(',')}] ${im.linkType}/${im.direction}/${im.magnitude} conf=${im.confidence} exp=$${(im.exposureUSD || 0).toLocaleString('en-US')} — "${im.evidenceQuote}" (${im.source})${im.rationale ? ' — ' + im.rationale : ''}`
    );
    sections.push('=== NEWS IMPACTS ON HELD POSITIONS (validated: quote is verbatim from source'
      + _capNoteSimple(impacts.length, iShown.length, 'impacts') + ') ===\n' + iLines.join('\n')
      + '\nRULE: treat impacts with conf>=0.6 as actionable context; below that, mention only as watch items. Cite the quote in reasoning. magnitude is qualitative — never turn it into a price %.');
  } else {
    // Fallback: raw headlines (P1 bridge) when the impact stage returned nothing
    const newsItems = (news && news.items) || [];
    if (newsItems.length) {
      const nShown = newsItems.slice(0, 12);
      const nLines = nShown.map(n => {
        const age = n.ts ? Math.round((Date.now() - n.ts) / 3600000) + 'h ago' : 'undated';
        const tks = n.tickers && n.tickers.length ? ` [${n.tickers.join(',')}]` : '';
        // The raw-headline fallback: text from the internet, one row per line. promptSafe.
        return `(${promptSafe(n.source, 32)}, ${age})${tks} ${promptSafe(n.title, 200)}`;
      });
      sections.push('=== RECENT NEWS (held instruments + TR macro'
        + _capNoteSimple(newsItems.length, nShown.length, 'headlines') + ') ===\n' + nLines.join('\n')
        + '\nRULE: use a headline only if it clearly relates to a held instrument; cite it in reasoning. Never invent impact from an unrelated headline.');
    }
  }

  return sections.join('\n\n');
}

// ─── RESPONSE PARSER ────────────────────────────────────────────

function _parseResponse(content) {
  try {
    // Strip markdown fences if present — closing fence optional (truncated responses)
    let json = content.trim();
    const fenceMatch = json.match(/```(?:json)?\s*([\s\S]*?)(?:```|$)/);
    if (fenceMatch) json = fenceMatch[1].trim();

    let parsed;
    try {
      parsed = JSON.parse(json);
    } catch (_e1) {
      // Fallback: widest {...} slice (tolerates pre/post chatter around the JSON)
      const braceMatch = json.match(/\{[\s\S]*\}/);
      if (!braceMatch) throw _e1;
      parsed = JSON.parse(braceMatch[0]);
    }
    return {
      recommendations: (parsed.recommendations || []).slice(0, MAX_RECOMMENDATIONS),
      alerts: (parsed.alerts || []).slice(0, MAX_ALERTS),
      marketSummary: parsed.marketSummary || '',
      portfolioHealth: parsed.portfolioHealth || 'unknown',
    };
  } catch (e) {
    console.warn('[Copilot] Failed to parse LLM response as JSON:', e);
    // Attempt to extract partial data
    return {
      recommendations: [],
      alerts: [],
      marketSummary: content.slice(0, 200),
      portfolioHealth: 'unknown',
      parseError: true,
    };
  }
}

// ─── RESULT STORAGE ─────────────────────────────────────────────

function _storeResults(parsed, taData, scanStart) {
  if (!S.aiCopilot) {
    S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  }

  const ts = new Date().toISOString();
  const scanId = uid();

  // Stamp recommendations with IDs and metadata, then apply MANDATE POLICY at the source
  // (locked 2026-07-21): exit-side directives on accumulate-mandate (Retirement) assets
  // become informational DCA guidance — never scoreable, never a sell call.
  const mandateCtx = { holdings: S.holdings || [], portfolios: S.portfolios || [], trades: S.trades || [], prices: S.prices || {}, fxRate: S.fxRate };
  const recs = (parsed.recommendations || []).map(r => transformForMandate({
    id: uid(),
    ...r,
    scanId,
    createdAt: ts,
    dismissed: false,
  }, mandateCtx));

  // Stamp alerts
  const alerts = (parsed.alerts || []).map(a => ({
    id: uid(),
    ...a,
    scanId,
    createdAt: ts,
    dismissed: false,
  }));

  // Merge: keep non-dismissed from previous scans, add new
  const prevAll = (S.aiCopilot.recommendations || []).slice(); // decision-ledger: snapshot before merge
  const prevRecs = (S.aiCopilot.recommendations || []).filter(r => !r.dismissed);
  const prevAlerts = (S.aiCopilot.alerts || []).filter(a => !a.dismissed);

  // Deduplicate: remove old recs for same ticker+type combo if new scan has replacement.
  // Mandate policy also applies to SURVIVING prior recs — a standing sell/tp card on an
  // accumulate asset converts to DCA guidance here instead of lingering as a directive
  // (dedup keys are ticker:type, so a transformed new rec would never replace the old one).
  const newKeys = new Set(recs.map(r => `${r.ticker}:${r.type}`));
  const filteredPrev = prevRecs.filter(r => !newKeys.has(`${r.ticker}:${r.type}`)).map(r => transformForMandate(r, mandateCtx));

  const newAlertKeys = new Set(alerts.map(a => `${a.ticker}:${a.type}`));
  const filteredPrevAlerts = prevAlerts.filter(a => !newAlertKeys.has(`${a.ticker}:${a.type}`));

  // Cache TA data for scenario engine (RSI, trend, etc.)
  cacheTAData(taData.data || {});

  S.aiCopilot.recommendations = [...recs, ...filteredPrev].slice(0, MAX_RECOMMENDATIONS);
  ledgerRecEmitted(recs);
  ledgerMergeDiff(prevAll, recs, S.aiCopilot.recommendations);
  S.aiCopilot.alerts = [...alerts, ...filteredPrevAlerts].slice(0, MAX_ALERTS);
  S.aiCopilot.lastScan = {
    id: scanId,
    ts,
    elapsed: Date.now() - scanStart,
    tickerCount: Object.keys(taData.data || {}).length,
    recCount: recs.length,
    alertCount: alerts.length,
    marketSummary: parsed.marketSummary || '',
    portfolioHealth: parsed.portfolioHealth || 'unknown',
  };

  // Scan history (capped)
  if (!S.aiCopilot.scanHistory) S.aiCopilot.scanHistory = [];
  S.aiCopilot.scanHistory.unshift({
    id: scanId,
    ts,
    tickerCount: Object.keys(taData.data || {}).length,
    recCount: recs.length,
    alertCount: alerts.length,
    health: parsed.portfolioHealth || 'unknown',
  });
  if (S.aiCopilot.scanHistory.length > MAX_SCAN_HISTORY) {
    S.aiCopilot.scanHistory = S.aiCopilot.scanHistory.slice(0, MAX_SCAN_HISTORY);
  }

  save();
  renderOnly('dash', 'advisory');
}

/**
 * Record WHY the impact stage did not refresh (audit 2026-08-01).
 *
 * analyzeNewsImpact bails on four paths, all of which leave S.aiCopilot.impacts and
 * .lastImpact untouched while the surrounding scan completes and stamps a fresh
 * lastScan.ts. The UI then renders 13-day-old evidence beside "scan just now" with no
 * way to tell the two apart. Annotating the EXISTING lastImpact record keeps the state
 * blob the same shape and size (no new field on a new object, no extra save()) — a
 * successful run replaces the record wholesale, which clears the marker automatically.
 */
function _noteImpactFailure(reason) {
  console.warn('[Copilot] Impact stage did not refresh:', reason);
  if (!S.aiCopilot) return;
  const prev = S.aiCopilot.lastImpact || {};
  S.aiCopilot.lastImpact = {
    ...prev,
    error: String(reason || 'unknown').slice(0, 200),
    errorTs: new Date().toISOString(),
  };
  // No save() here on purpose: _storeResults saves at the end of the same scan.
}

// ─── PUBLIC API ─────────────────────────────────────────────────

/**
 * Get active (non-dismissed) recommendations, sorted by urgency.
 */
export function getCopilotRecommendations() {
  const recs = (S.aiCopilot?.recommendations || []).filter(r => !r.dismissed && !r.acted);
  const urgencyOrder = { high: 0, medium: 1, low: 2 };
  return recs.sort((a, b) => (urgencyOrder[a.urgency] ?? 2) - (urgencyOrder[b.urgency] ?? 2));
}

/**
 * Get active (non-dismissed) alerts, sorted by severity.
 */
export function getCopilotAlerts() {
  const alerts = (S.aiCopilot?.alerts || []).filter(a => !a.dismissed);
  const sevOrder = { critical: 0, warning: 1, info: 2 };
  return alerts.sort((a, b) => (sevOrder[a.severity] ?? 2) - (sevOrder[b.severity] ?? 2));
}

/**
 * Dismiss a recommendation by ID.
 */
export function dismissRecommendation(id, reason = 'later') {
  const rec = (S.aiCopilot?.recommendations || []).find(r => r.id === id);
  if (rec) {
    rec.dismissed = true;
    rec.dismissedAt = new Date().toISOString();
    rec.dismissReason = reason; // mandate_mismatch | disagree | later
    ledgerRecDismissed(rec, reason);
    save();
    renderOnly('dash', 'advisory');
  }
}

/**
 * Dismiss an alert by ID.
 */
export function dismissAlert(id) {
  const alert = (S.aiCopilot?.alerts || []).find(a => a.id === id);
  if (alert) {
    alert.dismissed = true;
    alert.dismissedAt = new Date().toISOString();
    save();
    renderOnly('dash');
  }
}

/**
 * Get copilot scan status.
 */
export function getCopilotStatus() {
  return {
    scanning: _scanning,
    lastScan: S.aiCopilot?.lastScan || null,
    scanHistory: S.aiCopilot?.scanHistory || [],
    activeRecs: getCopilotRecommendations().length,
    activeAlerts: getCopilotAlerts().length,
  };
}

/**
 * Check if a scan is currently running.
 */
export function isCopilotScanning() {
  return _scanning;
}

/**
 * Clear all copilot data (reset).
 */
export function clearCopilotData() {
  S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  save();
  renderOnly('dash');
  toast('Copilot data cleared', 'a-g');
}

// ─── DCA OPPORTUNITY SCANNER (client-side, no LLM) ─────────────
//
// Detects holdings with significant unrealized loss and generates
// averaging-down scenarios at multiple budget levels.
// Runs locally — call on every price refresh for real-time monitoring.

const DCA_LOSS_THRESHOLD = -15; // Show opportunities for holdings down >15%
const DCA_BUDGETS = [250, 500, 1000]; // USD budget levels for scenarios

/**
 * Scan all holdings + open trades for DCA averaging-down opportunities.
 * Pure math — no LLM, no API calls. Runs on every price refresh.
 *
 * @returns {Array<Object>} opportunities sorted by loss % (worst first)
 *   Each: { ticker, type, currency, qty, avgCost, currentPrice, valueUSD,
 *           investedUSD, pnlUSD, pnlPct, scenarios: [{budget, addQty, newAvgCost,
 *           newAvgCostLocal, breakeven, breakevenPct, impactPct}], support, resistance }
 */
export function scanDCAOpportunities() {
  // No rate, no comparable USD figures — the caller shows nothing rather than parity numbers.
  const fxR = fxUSDTRY();
  const fxE = fxEURTRY();
  if (fxR == null || fxE == null) return [];
  const opportunities = [];

  // Scan holdings
  const safeH = (S.holdings || []).filter(h => h && !h._del && h.qty > 0);
  safeH.forEach(h => {
    const v = hVal(h);
    if (!v || !v.vU || !v.px) return;
    const inv = hTotUSD(h);
    if (inv <= 0) return;
    const pnlUSD = v.vU - inv;
    const pnlPct = (pnlUSD / inv) * 100;

    if (pnlPct > DCA_LOSS_THRESHOLD) return; // Not a loser

    const cur = h.currency || 'USD';
    const px = v.px; // current price in local currency
    const avgCost = h.avgCost || (inv / h.qty * (cur === 'USD' ? 1 : cur === 'TRY' ? fxR : fxE));

    // Build scenarios at each budget level
    const scenarios = DCA_BUDGETS.map(budgetUSD => {
      // Convert budget to local currency for price calculation
      const budgetLocal = cur === 'USD' ? budgetUSD : cur === 'TRY' ? budgetUSD * fxR : budgetUSD * fxE;
      const addQty = px > 0 ? budgetLocal / px : 0;
      const totalQty = h.qty + addQty;
      const totalCostLocal = (h.qty * avgCost) + (addQty * px);
      const newAvgCostLocal = totalQty > 0 ? totalCostLocal / totalQty : avgCost;
      // How much does avg cost drop?
      const costDropPct = avgCost > 0 ? ((avgCost - newAvgCostLocal) / avgCost) * 100 : 0;
      // Breakeven: what price does it need to reach from current to break even?
      const breakevenPct = px > 0 ? ((newAvgCostLocal - px) / px) * 100 : 0;
      // New avg cost in USD for display
      const newAvgCostUSD = cur === 'USD' ? newAvgCostLocal :
        cur === 'TRY' ? newAvgCostLocal / fxR : newAvgCostLocal / fxE;
      const avgCostUSD = cur === 'USD' ? avgCost :
        cur === 'TRY' ? avgCost / fxR : avgCost / fxE;

      return {
        budget: budgetUSD,
        addQty: +addQty.toFixed(6),
        newAvgCost: +newAvgCostLocal.toFixed(4),
        newAvgCostUSD: +newAvgCostUSD.toFixed(4),
        oldAvgCostUSD: +avgCostUSD.toFixed(4),
        costDropPct: +costDropPct.toFixed(1),
        breakevenPct: +breakevenPct.toFixed(1),
      };
    });

    // Get TA data if cached (support/resistance levels)
    const taCache = S.aiCopilot?._taCache?.[h.ticker] || {};

    opportunities.push({
      ticker: h.ticker,
      type: h.type || 'Other',
      currency: cur,
      qty: h.qty,
      avgCost: +avgCost.toFixed(4),
      currentPrice: +px.toFixed(4),
      valueUSD: +v.vU.toFixed(2),
      investedUSD: +inv.toFixed(2),
      pnlUSD: +pnlUSD.toFixed(2),
      pnlPct: +pnlPct.toFixed(1),
      scenarios,
      support: taCache.support || null,
      resistance: taCache.resistance || null,
      sma200: taCache.sma200 || null,
      rsi: taCache.rsi || null,
      trend: taCache.trend || null,
    });
  });

  // Scan open trades (non-TD)
  const safeT = (S.trades || []).filter(t => t && !t._del && t.status !== 'Closed' && t.type !== 'TimeDeposit');
  safeT.forEach(t => {
    const px = S.prices?.[t.ticker]?.price;
    if (!px || !t.entry || !t.qty) return;
    const isLong = (t.side || 'Long') === 'Long';
    const lev = t.leverage || 1;
    const cur = t.currency || 'USD';

    // Only long positions can be averaged down
    if (!isLong) return;

    const costUSD = _toUSD(t.entry * t.qty, cur);
    const curUSD = _toUSD(px * t.qty, cur);
    const pnlUSD = (curUSD - costUSD) * lev;
    const pnlPct = costUSD > 0 ? (pnlUSD / costUSD) * 100 : 0;

    if (pnlPct > DCA_LOSS_THRESHOLD) return;

    const scenarios = DCA_BUDGETS.map(budgetUSD => {
      // S.fxRateEUR is EUR/TRY, not EUR/USD. Multiplying a USD budget by it produced a
      // "EUR" figure ~52x too large, and the divisions below then produced a "USD" average
      // ~52x too small — the two errors are in the same block and do not cancel. Every EUR
      // leg goes through TRY, which is the only pair both rates share. (audit 2026-08-03)
      const budgetLocal = cur === 'USD' ? budgetUSD : cur === 'TRY' ? budgetUSD * fxR : budgetUSD * fxR / fxE;
      const addQty = px > 0 ? budgetLocal / px : 0;
      const totalQty = t.qty + addQty;
      const totalCost = (t.qty * t.entry) + (addQty * px);
      const newAvg = totalQty > 0 ? totalCost / totalQty : t.entry;
      const costDropPct = t.entry > 0 ? ((t.entry - newAvg) / t.entry) * 100 : 0;
      const breakevenPct = px > 0 ? ((newAvg - px) / px) * 100 : 0;
      const newAvgUSD = cur === 'USD' ? newAvg : cur === 'TRY' ? newAvg / fxR : newAvg * fxE / fxR;
      const oldAvgUSD = cur === 'USD' ? t.entry : cur === 'TRY' ? t.entry / fxR : t.entry * fxE / fxR;

      return {
        budget: budgetUSD,
        addQty: +addQty.toFixed(6),
        newAvgCost: +newAvg.toFixed(4),
        newAvgCostUSD: +newAvgUSD.toFixed(4),
        oldAvgCostUSD: +oldAvgUSD.toFixed(4),
        costDropPct: +costDropPct.toFixed(1),
        breakevenPct: +breakevenPct.toFixed(1),
      };
    });

    const taCache = S.aiCopilot?._taCache?.[t.ticker] || {};

    opportunities.push({
      ticker: t.ticker + (lev > 1 ? ` ${lev}x` : ''),
      type: t.type || 'Trade',
      currency: cur,
      qty: t.qty,
      avgCost: t.entry,
      currentPrice: px,
      valueUSD: +curUSD.toFixed(2),
      investedUSD: +costUSD.toFixed(2),
      pnlUSD: +pnlUSD.toFixed(2),
      pnlPct: +pnlPct.toFixed(1),
      scenarios,
      support: taCache.support || null,
      resistance: taCache.resistance || null,
      sma200: taCache.sma200 || null,
      rsi: taCache.rsi || null,
      trend: taCache.trend || null,
      isTrade: true,
      leverage: lev,
    });
  });

  // Sort by P&L% ascending (worst loss first)
  opportunities.sort((a, b) => a.pnlPct - b.pnlPct);

  return opportunities;
}

// ─── SMART ALERT GENERATOR (client-side, no LLM) ───────────────

/**
 * Generate quick alerts from current prices vs trade SL/TP levels.
 * Runs locally without LLM — call on every price refresh for real-time monitoring.
 */
export function generateQuickAlerts() {
  if (!S.aiCopilot) {
    S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  }

  const safeT = (S.trades || []).filter(t => t && !t._del && t.status !== 'Closed' && t.type !== 'TimeDeposit');
  const newAlerts = [];
  const ts = new Date().toISOString();

  safeT.forEach(t => {
    const px = S.prices?.[t.ticker]?.price;
    if (!px || !t.entry) return;

    const isLong = (t.side || 'Long') === 'Long';
    const lev = t.leverage || 1;

    // SL proximity check
    if (t.sl) {
      const distPct = isLong
        ? ((px - t.sl) / px) * 100
        : ((t.sl - px) / px) * 100;

      if (distPct <= 3 && distPct > 0) {
        newAlerts.push({
          id: uid(),
          type: 'sl_proximity',
          ticker: t.ticker,
          severity: distPct <= 1 ? 'critical' : 'warning',
          message: `${t.ticker} is ${distPct.toFixed(1)}% from SL (${t.sl}). Current: ${f(px)}. Leverage: ${lev}x. Effective risk: ${(distPct * lev).toFixed(1)}%.`,
          createdAt: ts,
          dismissed: false,
        });
      } else if (distPct <= 0) {
        newAlerts.push({
          id: uid(),
          type: 'sl_proximity',
          ticker: t.ticker,
          severity: 'critical',
          message: `${t.ticker} has BREACHED SL (${t.sl}). Current: ${f(px)}. Consider immediate exit.`,
          createdAt: ts,
          dismissed: false,
        });
      }
    }

    // TP1 proximity check
    if (t.tp1) {
      const distPct = isLong
        ? ((t.tp1 - px) / px) * 100
        : ((px - t.tp1) / px) * 100;

      if (distPct <= 5 && distPct > 0) {
        newAlerts.push({
          id: uid(),
          type: 'tp_proximity',
          ticker: t.ticker,
          severity: distPct <= 2 ? 'warning' : 'info',
          message: `${t.ticker} is ${distPct.toFixed(1)}% from TP1 (${t.tp1}). Current: ${f(px)}. Consider partial take-profit.`,
          createdAt: ts,
          dismissed: false,
        });
      } else if (distPct <= 0) {
        newAlerts.push({
          id: uid(),
          type: 'tp_proximity',
          ticker: t.ticker,
          severity: 'warning',
          message: `${t.ticker} has REACHED TP1 (${t.tp1}). Current: ${f(px)}. Take profit opportunity.`,
          createdAt: ts,
          dismissed: false,
        });
      }
    }

    // TP2 proximity check
    if (t.tp2) {
      const distPct = isLong
        ? ((t.tp2 - px) / px) * 100
        : ((px - t.tp2) / px) * 100;

      if (distPct <= 3 && distPct > 0) {
        newAlerts.push({
          id: uid(),
          type: 'tp_proximity',
          ticker: t.ticker,
          severity: 'warning',
          message: `${t.ticker} is ${distPct.toFixed(1)}% from TP2 (${t.tp2}). Current: ${f(px)}. Full exit target approaching.`,
          createdAt: ts,
          dismissed: false,
        });
      }
    }
  });

  // Merge new quick alerts (dedup by ticker+type, prefer newest)
  if (newAlerts.length) {
    const existing = (S.aiCopilot.alerts || []).filter(a => !a.dismissed);
    const quickAlertKeys = new Set(newAlerts.map(a => `${a.ticker}:${a.type}`));
    const kept = existing.filter(a => !quickAlertKeys.has(`${a.ticker}:${a.type}`));
    S.aiCopilot.alerts = [...newAlerts, ...kept].slice(0, MAX_ALERTS);
    save();
  }

  return newAlerts;
}

// ─── SCENARIO PLAYBOOK ENGINE ──────────────────────────────────
//
// Conditional rules: "if X → suggest Y". Evaluated client-side on
// every price refresh. When a scenario triggers, it injects an alert
// into S.aiCopilot.alerts with source='scenario'.
//
// Condition types:
//   price_above / price_below   — ticker price vs threshold
//   rsi_above / rsi_below       — TA RSI vs threshold (needs TA data)
//   pnl_above / pnl_below       — open trade unrealized P&L % vs threshold
//   weight_drift                 — |actual − target| weight exceeds threshold
//   fear_greed_above/below       — crypto F&G index vs threshold
//   drawdown_exceeds             — portfolio drawdown from peak exceeds %
//
// Each scenario can have multiple conditions (AND logic).

const SCENARIO_COOLDOWN_DEFAULT = 60 * 60 * 1000; // 1h between re-triggers
const MAX_SCENARIOS = 30;

/**
 * Preset scenario templates the user can activate with one click.
 */
export const SCENARIO_PRESETS = [
  {
    key: 'oversold_buy',
    name: 'Oversold DCA Opportunity',
    description: 'Alert when any portfolio holding RSI drops below 30',
    conditions: [{ type: 'rsi_below', ticker: '*', value: 30 }],
    action: { type: 'buy', title: 'Oversold — consider extra DCA', urgency: 'high' },
  },
  {
    key: 'overbought_warn',
    name: 'Overbought Warning',
    description: 'Alert when any holding RSI rises above 70',
    conditions: [{ type: 'rsi_above', ticker: '*', value: 70 }],
    action: { type: 'take_profit', title: 'Overbought — review position', urgency: 'medium' },
  },
  {
    key: 'extreme_fear',
    name: 'Extreme Fear Buy Signal',
    description: 'Alert when Crypto Fear & Greed index drops below 20',
    conditions: [{ type: 'fear_greed_below', value: 20 }],
    action: { type: 'scale_in', title: 'Extreme fear — scale into crypto', urgency: 'high' },
  },
  {
    key: 'extreme_greed',
    name: 'Extreme Greed Warning',
    description: 'Alert when Crypto Fear & Greed index rises above 80',
    conditions: [{ type: 'fear_greed_above', value: 80 }],
    action: { type: 'alert', title: 'Extreme greed — tighten stops', urgency: 'medium' },
  },
  {
    key: 'weight_drift_rebalance',
    name: 'Allocation Drift Alert',
    description: 'Alert when any holding drifts >5% from target weight',
    conditions: [{ type: 'weight_drift', ticker: '*', value: 5 }],
    action: { type: 'rebalance', title: 'Weight drift detected — rebalance', urgency: 'medium' },
  },
  {
    key: 'btc_below_threshold',
    name: 'BTC Price Drop Alert',
    description: 'Alert when BTC drops below a custom price',
    conditions: [{ type: 'price_below', ticker: 'BTC', value: 90000 }],
    action: { type: 'buy', title: 'BTC below threshold — buying opportunity', urgency: 'high' },
  },
  {
    key: 'trade_pnl_danger',
    name: 'Trade Loss Warning',
    description: 'Alert when any open trade P&L drops below -10%',
    conditions: [{ type: 'pnl_below', ticker: '*', value: -10 }],
    action: { type: 'stop_loss', title: 'Trade losing >10% — review exit', urgency: 'high' },
  },
  {
    key: 'drawdown_alert',
    name: 'Portfolio Drawdown Alert',
    description: 'Alert when portfolio drawdown from peak exceeds 10%',
    conditions: [{ type: 'drawdown_exceeds', value: 10 }],
    action: { type: 'alert', title: 'Portfolio drawdown >10% — defensive review', urgency: 'high' },
  },
];

/**
 * Create a new scenario (custom or from preset).
 * @param {Object} def — { name, conditions, action, cooldownMs?, enabled? }
 * @returns {Object} the created scenario
 */
export function createScenario(def) {
  if (!S.aiCopilot) {
    S.aiCopilot = { recommendations: [], scenarios: [], alerts: [], lastScan: null, scanHistory: [] };
  }
  if (!S.aiCopilot.scenarios) S.aiCopilot.scenarios = [];

  if (S.aiCopilot.scenarios.length >= MAX_SCENARIOS) {
    toast(`Max ${MAX_SCENARIOS} scenarios reached`, 'a-y');
    return null;
  }

  const scenario = {
    id: uid(),
    name: def.name || 'Untitled Scenario',
    description: def.description || '',
    conditions: def.conditions || [],
    action: def.action || { type: 'alert', title: 'Scenario triggered', urgency: 'medium' },
    cooldownMs: def.cooldownMs ?? SCENARIO_COOLDOWN_DEFAULT,
    enabled: def.enabled !== false,
    presetKey: def.presetKey || null,
    createdAt: new Date().toISOString(),
    lastTriggered: null,
    triggerCount: 0,
  };

  S.aiCopilot.scenarios.push(scenario);
  save();
  renderOnly('dash');
  toast(`Scenario "${scenario.name}" created`, 'a-g');
  return scenario;
}

/**
 * Activate a preset scenario by key.
 * @param {string} key — preset key (e.g. 'oversold_buy')
 * @param {Object} overrides — optional field overrides (e.g. { conditions: [...] })
 */
export function activatePreset(key, overrides = {}) {
  const preset = SCENARIO_PRESETS.find(p => p.key === key);
  if (!preset) { toast('Unknown preset', 'a-r'); return null; }

  // Check if already activated
  const existing = (S.aiCopilot?.scenarios || []).find(s => s.presetKey === key && !s._del);
  if (existing) {
    toast('Preset already active', 'a-y');
    return existing;
  }

  return createScenario({
    ...preset,
    ...overrides,
    presetKey: key,
  });
}

/**
 * Update a scenario.
 * @param {string} id
 * @param {Object} updates — partial fields to merge
 */
export function updateScenario(id, updates) {
  const sc = (S.aiCopilot?.scenarios || []).find(s => s.id === id);
  if (!sc) return;
  Object.assign(sc, updates, { _updatedAt: new Date().toISOString() });
  save();
  renderOnly('dash');
}

/**
 * Toggle a scenario on/off.
 */
export function toggleScenario(id) {
  const sc = (S.aiCopilot?.scenarios || []).find(s => s.id === id);
  if (!sc) return;
  sc.enabled = !sc.enabled;
  save();
  renderOnly('dash');
  toast(`Scenario "${sc.name}" ${sc.enabled ? 'enabled' : 'paused'}`, 'a-g');
}

/**
 * Delete a scenario (soft-delete).
 */
export function deleteScenario(id) {
  const sc = (S.aiCopilot?.scenarios || []).find(s => s.id === id);
  if (!sc) return;
  sc._del = true;
  save();
  renderOnly('dash');
  toast(`Scenario deleted`, 'a-g');
}

/**
 * Get all active (non-deleted) scenarios.
 */
export function getScenarios() {
  return (S.aiCopilot?.scenarios || []).filter(s => !s._del);
}

/**
 * Get preset availability — which presets are active vs available.
 */
export function getPresetStatus() {
  const active = new Set((S.aiCopilot?.scenarios || []).filter(s => !s._del).map(s => s.presetKey).filter(Boolean));
  return SCENARIO_PRESETS.map(p => ({
    ...p,
    active: active.has(p.key),
  }));
}

// ─── SCENARIO EVALUATOR ────────────────────────────────────────

/**
 * Evaluate all enabled scenarios against current state.
 * Call on every price refresh for real-time monitoring.
 * Returns array of triggered alerts injected into S.aiCopilot.alerts.
 */
export function evaluateScenarios() {
  if (!S.aiCopilot?.scenarios) return [];

  const now = Date.now();
  const scenarios = S.aiCopilot.scenarios.filter(s => !s._del && s.enabled);
  if (!scenarios.length) return [];

  const triggered = [];
  const ts = new Date().toISOString();

  // Build evaluation context once (cached for all scenarios)
  const ctx = _buildEvalContext();

  for (const sc of scenarios) {
    // Cooldown check
    if (sc.lastTriggered) {
      const elapsed = now - new Date(sc.lastTriggered).getTime();
      if (elapsed < (sc.cooldownMs || SCENARIO_COOLDOWN_DEFAULT)) continue;
    }

    // Evaluate conditions — find matching tickers
    const matches = _evaluateConditions(sc.conditions, ctx);
    if (!matches.length) continue;

    // Scenario triggered — generate alerts
    for (const match of matches) {
      const alert = {
        id: uid(),
        type: 'scenario_trigger',
        ticker: match.ticker || sc.action.ticker || '—',
        severity: sc.action.urgency === 'high' ? 'critical' : sc.action.urgency === 'medium' ? 'warning' : 'info',
        message: `[Scenario] ${sc.name}: ${sc.action.title}${match.detail ? ` (${match.detail})` : ''}`,
        source: 'scenario',
        scenarioId: sc.id,
        createdAt: ts,
        dismissed: false,
      };
      triggered.push(alert);
    }

    // Update scenario metadata
    sc.lastTriggered = ts;
    sc.triggerCount = (sc.triggerCount || 0) + 1;
  }

  // Inject triggered alerts
  if (triggered.length) {
    const existing = (S.aiCopilot.alerts || []).filter(a => !a.dismissed);
    // Remove previous scenario alerts for same scenario (avoid duplicates)
    const triggeredScIds = new Set(triggered.map(a => a.scenarioId));
    const kept = existing.filter(a => !(a.source === 'scenario' && triggeredScIds.has(a.scenarioId)));
    S.aiCopilot.alerts = [...triggered, ...kept].slice(0, MAX_ALERTS);
    save();
    renderOnly('dash');
  }

  return triggered;
}

/**
 * Build evaluation context from current state — prices, trades, portfolio.
 */
function _buildEvalContext() {
  const prices = S.prices || {};
  const safeH = (S.holdings || []).filter(h => h && !h._del);
  const safeT = _openTrades(S.trades);
  const fxR = fxUSDTRY();

  // Portfolio weights + drift
  const holdingMap = {};
  safeH.forEach(h => {
    try {
      const v = hVal(h);
      holdingMap[h.ticker] = {
        ticker: h.ticker, type: h.type, currency: h.currency,
        valueUSD: v?.vU || 0,
        targetWeight: h.allocationPct || 0,
        pnlPct: (() => { try { const p = hPnl(h); return p?.pnlP || 0; } catch (_) { return 0; } })(),
      };
    } catch (_) { /* skip */ }
  });
  // Same-set denominator as above (audit 2026-08-01).
  const totalUSD = Object.values(holdingMap).reduce((s, h) => s + (h.valueUSD || 0), 0);
  Object.values(holdingMap).forEach(h => {
    h.actualWeight = totalUSD > 0 ? (h.valueUSD / totalUSD) * 100 : 0;
    h.weightDrift = h.targetWeight > 0 ? Math.abs(h.actualWeight - h.targetWeight) : 0;
  });

  // Trade P&L map
  const tradeMap = {};
  safeT.forEach(t => {
    const px = prices[t.ticker]?.price;
    if (!px || !t.entry) return;
    const isLong = (t.side || 'Long') === 'Long';
    const lev = t.leverage || 1;
    const pnlPct = ((isLong ? (px - t.entry) : (t.entry - px)) / t.entry) * 100 * lev;
    if (!tradeMap[t.ticker]) tradeMap[t.ticker] = [];
    tradeMap[t.ticker].push({ ticker: t.ticker, pnlPct, entry: t.entry, px, lev, side: t.side || 'Long' });
  });

  // TA cache (from last scan)
  const taCache = S.aiCopilot?._taCache || {};

  // Fear & Greed
  const fg = S.aiCopilot?._fgCache || null;

  // Portfolio peak tracking for drawdown
  const peakVal = S.aiCopilot?._portfolioPeak || totalUSD;
  if (totalUSD > peakVal) {
    S.aiCopilot._portfolioPeak = totalUSD;
  }
  const drawdownPct = peakVal > 0 ? ((peakVal - totalUSD) / peakVal) * 100 : 0;

  return {
    prices, holdingMap, tradeMap, taCache, fg,
    totalUSD, fxR, drawdownPct,
    allTickers: [...new Set([...Object.keys(holdingMap), ...Object.keys(tradeMap)])],
  };
}

/**
 * Evaluate a set of conditions (AND logic). Returns array of matches.
 * Wildcard ticker '*' expands to all relevant tickers.
 */
function _evaluateConditions(conditions, ctx) {
  if (!conditions.length) return [];

  // For AND logic: all conditions must match. For wildcard tickers,
  // we find the intersection of matching tickers across all conditions.
  const condResults = conditions.map(cond => {
    const matches = _evalSingleCondition(cond, ctx);
    return matches; // array of { ticker, detail }
  });

  // AND: intersect by ticker
  if (condResults.length === 1) return condResults[0];

  // Multi-condition: intersect
  let result = condResults[0];
  for (let i = 1; i < condResults.length; i++) {
    const nextTickers = new Set(condResults[i].map(m => m.ticker));
    result = result.filter(m => nextTickers.has(m.ticker));
  }

  // Merge details
  return result.map(m => {
    const allDetails = condResults
      .flatMap(cr => cr.filter(x => x.ticker === m.ticker))
      .map(x => x.detail)
      .filter(Boolean);
    return { ticker: m.ticker, detail: allDetails.join('; ') };
  });
}

/**
 * Evaluate a single condition. Returns array of { ticker, detail } matches.
 */
function _evalSingleCondition(cond, ctx) {
  const matches = [];
  const tickers = cond.ticker === '*' ? ctx.allTickers : [cond.ticker];

  for (const ticker of tickers) {
    switch (cond.type) {
      case 'price_above': {
        const px = ctx.prices[ticker]?.price;
        if (px != null && px > cond.value) {
          matches.push({ ticker, detail: `price ${f(px)} > ${f(cond.value)}` });
        }
        break;
      }
      case 'price_below': {
        const px = ctx.prices[ticker]?.price;
        if (px != null && px < cond.value) {
          matches.push({ ticker, detail: `price ${f(px)} < ${f(cond.value)}` });
        }
        break;
      }
      case 'rsi_above': {
        const rsi = ctx.taCache[ticker]?.rsi;
        if (rsi != null && rsi > cond.value) {
          matches.push({ ticker, detail: `RSI ${rsi.toFixed(1)} > ${cond.value}` });
        }
        break;
      }
      case 'rsi_below': {
        const rsi = ctx.taCache[ticker]?.rsi;
        if (rsi != null && rsi < cond.value) {
          matches.push({ ticker, detail: `RSI ${rsi.toFixed(1)} < ${cond.value}` });
        }
        break;
      }
      case 'pnl_above': {
        const trades = ctx.tradeMap[ticker] || [];
        for (const t of trades) {
          if (t.pnlPct > cond.value) {
            matches.push({ ticker, detail: `P&L ${t.pnlPct.toFixed(1)}% > ${cond.value}%` });
          }
        }
        break;
      }
      case 'pnl_below': {
        const trades = ctx.tradeMap[ticker] || [];
        for (const t of trades) {
          if (t.pnlPct < cond.value) {
            matches.push({ ticker, detail: `P&L ${t.pnlPct.toFixed(1)}% < ${cond.value}%` });
          }
        }
        break;
      }
      case 'weight_drift': {
        const h = ctx.holdingMap[ticker];
        if (h && h.weightDrift > cond.value) {
          matches.push({ ticker, detail: `drift ${h.weightDrift.toFixed(1)}% > ${cond.value}%` });
        }
        break;
      }
      case 'fear_greed_below': {
        const val = ctx.fg?.value;
        if (val != null && val < cond.value) {
          matches.push({ ticker: 'MARKET', detail: `F&G ${val} < ${cond.value}` });
        }
        break;
      }
      case 'fear_greed_above': {
        const val = ctx.fg?.value;
        if (val != null && val > cond.value) {
          matches.push({ ticker: 'MARKET', detail: `F&G ${val} > ${cond.value}` });
        }
        break;
      }
      case 'drawdown_exceeds': {
        if (ctx.drawdownPct > cond.value) {
          matches.push({ ticker: 'PORTFOLIO', detail: `drawdown ${ctx.drawdownPct.toFixed(1)}% > ${cond.value}%` });
        }
        break;
      }
      default:
        break;
    }
  }

  return matches;
}

/**
 * Cache TA data after a scan so scenarios can use RSI etc.
 * Called from _storeResults.
 */
export function cacheTAData(taData) {
  if (!S.aiCopilot) return;
  // Timestamp lives OUTSIDE the cache object: _taCache is keyed by ticker and is
  // iterated elsewhere, so a ts key inside it would read as an instrument.
  // Without this the integrity watchdog can only report the cache as UNKNOWN.
  S.aiCopilot._taCacheTs = Date.now();
  S.aiCopilot._taCache = {};
  Object.entries(taData).forEach(([ticker, d]) => {
    if (!d.error) {
      S.aiCopilot._taCache[ticker] = {
        rsi: d.rsi, trend: d.trend,
        sma20: d.sma20, sma50: d.sma50, sma200: d.sma200,
        support: d.support, resistance: d.resistance,
      };
    }
  });
}

/**
 * Cache Fear & Greed value for scenario evaluation.
 */
export function cacheFearGreed(fg) {
  if (!S.aiCopilot) return;
  S.aiCopilot._fgCache = fg || null;
  // Fetch time, not the index's own timestamp — the Bonus planner picks a deployment
  // profile from this, so its age has to be checkable.
  S.aiCopilot._fgCacheTs = fg ? Date.now() : null;
}
